import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  ButtonBase,
  Modal,
} from '@material-ui/core';
import { CreateCSSProperties } from '@material-ui/core/styles/withStyles';
import { useSelector } from 'react-redux';
import { selectImageViewerInitial } from 'models/twilio';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
} from '@material-ui/icons';
import SwipeableViews from 'react-swipeable-views';
import ColoredMaterialIcon from './styled/ColoredMaterialIcon';
import useConverastionImages from '../hooks/useConversationImages';
import useMutableRef from '../hooks/useMutableRef';
import { BEHAVIOR } from '../constants';
import SwipeableImage from './SwipeableImage';

const ViewerContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  outline: none;
  position: relative;
`;

const ChevronButton = styled(ButtonBase)`
  flex: 0 0 ${BEHAVIOR.CHEVRON_SIZE}px;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);

  &:disabled {
    cursor: not-allowed;
    pointer-events: all;
  }

  @media only screen and (max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ImageViewContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: calc(100% - ${BEHAVIOR.CHEVRON_SIZE * 2}px);
  height: 100%;
  padding: 56px;

  @media only screen and (max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px) {
    width: 100%;
    padding: 0;
  }
`;

const chevronOverrides: CreateCSSProperties = {
  height: 48,
  width: 48,
};
const swipableViewStyles = { height: '100%' };

const ConversationImageViewer: FC = () => {
  const prevButtonRef = useRef<HTMLButtonElement>();
  const nextButtonRef = useRef<HTMLButtonElement>();
  const initialMessageId = useSelector(selectImageViewerInitial);
  const imageMessages = useConverastionImages();
  const imageMessageRef = useMutableRef(imageMessages);
  const [imageIndex, setImageIndex] = useState<number>(null);
  const numMessages = imageMessages.length;

  const goPrev = useCallback(() => setImageIndex((prev) => prev - 1), []);
  const goNext = useCallback(() => setImageIndex((prev) => prev + 1), []);
  const onChangeIndex = useCallback((index: number, _indexLatest: number) => {
    setImageIndex(index);
  }, []);

  const getIndexFromMessageId = useCallback((id: string) => {
    const messages = imageMessageRef.current;
    const targetIndex = messages.findIndex((message) => message.id === id);
    return targetIndex < 0 ? null : targetIndex;
  }, [imageMessageRef]);

  const onKeyDownHandler = useCallback(({ key }: KeyboardEvent) => {
    if (key === 'ArrowLeft') prevButtonRef.current?.click();
    if (key === 'ArrowRight') nextButtonRef.current?.click();
  }, []);

  useEffect(() => {
    if (initialMessageId) {
      document.addEventListener('keydown', onKeyDownHandler);
      const messageIndex = getIndexFromMessageId(initialMessageId);
      setImageIndex(messageIndex);
    }
    return () => document.removeEventListener('keydown', onKeyDownHandler);
  }, [initialMessageId, getIndexFromMessageId, onKeyDownHandler]);

  return (
    <Modal
      open={Boolean(initialMessageId)}
      aria-labelledby="conversation-image-viewer"
      aria-describedby="displays-all-images-in-a-conversation"
    >
      <ViewerContainer>
        <ChevronButton ref={prevButtonRef} onClick={goPrev} disabled={!imageIndex}>
          <ColoredMaterialIcon Icon={ChevronLeftRounded} color="white" overrides={chevronOverrides} />
        </ChevronButton>
        <ImageViewContainer>
          <SwipeableViews
            ignoreNativeScroll
            enableMouseEvents
            resistance
            index={imageIndex}
            onChangeIndex={onChangeIndex}
            style={swipableViewStyles}
            containerStyle={swipableViewStyles}
          >
            {imageMessages.map((imageMessage) => (
              <SwipeableImage key={`Swipeable-${imageMessage.id}`} message={imageMessage} />
            ))}
          </SwipeableViews>
        </ImageViewContainer>
        <ChevronButton ref={nextButtonRef} onClick={goNext} disabled={imageIndex === numMessages - 1}>
          <ColoredMaterialIcon Icon={ChevronRightRounded} color="white" overrides={chevronOverrides} />
        </ChevronButton>
      </ViewerContainer>
    </Modal>
  );
};

export default React.memo(ConversationImageViewer);
